import * as Sentry from '@sentry/browser'
import type { TypedStartListening } from '@reduxjs/toolkit'

import { getMeRequest, getMeSuccess, getMeFailure } from './authActions'

import { analytics } from '#entities/analytics'
import { addUser } from '#ducks/users'
import { describeFailure, getMe, type GetMeStatus200 } from '#shared/api'
import { restartOnHome } from '#shared/api/authNavigation'
import type { All } from '#types/store'

// Keep the lead within the backend's one-minute token refresh window.
export const REFRESH_LEAD_MS = 30_000

export const RETRY_DELAY_MS = 5_000

export function addListeners(startListener: TypedStartListening<All>) {
  startListener({
    actionCreator: getMeRequest,
    effect: async (_action, listenerApi) => {
      // getOriginalState() must be read before the first await.
      const { isLoading, isBootstrapped: wasBootstrapped, userId: previousUserId } = listenerApi.getOriginalState().auth

      if (isLoading) {
        return
      }

      const result = await getMe()
      if (result.kind !== 'success') {
        console.error('GET /api/auth/me failed', describeFailure(result))
        listenerApi.dispatch(getMeFailure())
        return
      }

      const { user, accessToken, expiresAt }: GetMeStatus200 = result.data

      // The live socket is bound to the previous identity; switching users requires a reload.
      if (wasBootstrapped && user.sub !== previousUserId) {
        restartOnHome()
        return
      }

      listenerApi.dispatch(addUser(user))

      Sentry.setUser({ id: user.sub, username: user.name ?? undefined })

      // Preserve the existing analytics segment name.
      analytics.setUserProperties({ id: user.sub, isTemporary: user.accountType === 'guest' })
      if (!wasBootstrapped) {
        analytics.logEvent('User authorized')
      }

      listenerApi.dispatch(getMeSuccess({ userId: user.sub, accessToken, expiresAt, accountType: user.accountType }))
    },
  })

  startListener({
    actionCreator: getMeSuccess,
    effect: async ({ payload }, listenerApi) => {
      listenerApi.cancelActiveListeners()
      // Clock skew can return the same expiry; enforce a minimum delay to avoid a refresh loop.
      await listenerApi.delay(Math.max(RETRY_DELAY_MS, payload.expiresAt - Date.now() - REFRESH_LEAD_MS))
      listenerApi.dispatch(getMeRequest())
    },
  })

  startListener({
    actionCreator: getMeFailure,
    effect: async (_action, listenerApi) => {
      listenerApi.cancelActiveListeners()
      await listenerApi.delay(RETRY_DELAY_MS)
      listenerApi.dispatch(getMeRequest())
    },
  })
}
